/**
 * Mixpanel Analytics Configuration
 * World-class analytics setup for Compudopt web-helper
 */

export const MIXPANEL_CONFIG = {
  token: process.env.REACT_APP_MIXPANEL_TOKEN,
  debug: process.env.NODE_ENV === 'development',
  track_pageview: true,
  persistence: 'localStorage',
  ignore_dnt: false, // Respect Do Not Track
  batch_requests: false, // Disable batching for immediate event delivery
  batch_size: 1,
  batch_flush_interval_ms: 1000,
  api_host: 'https://api.mixpanel.com',

  // Privacy settings
  ip: false, // Don't track IP
  property_blacklist: ['$email', '$phone', '$name'],
};

/**
 * Standardized event names for consistent tracking
 */
export const MIXPANEL_EVENTS = {
  // Session Events
  SESSION_START: 'Session Started',
  SESSION_END: 'Session Ended',

  // Chat Events
  CHAT_OPENED: 'Chat Opened',
  CHAT_CLOSED: 'Chat Closed',
  MESSAGE_SENT: 'Message Sent',
  MESSAGE_RECEIVED: 'Message Received',

  // Flow Events
  FLOW_STARTED: 'Flow Started',
  FLOW_COMPLETED: 'Flow Completed',
  FLOW_ABANDONED: 'Flow Abandoned',

  // Engagement Events
  BUTTON_CLICKED: 'Button Clicked',
  EXPLORE_MORE_CLICKED: 'Explore More Clicked',
  EXPLORE_MORE_SELECTED: 'Explore More Selected',
  EXTERNAL_LINK_CLICKED: 'External Link Clicked',
  LANGUAGE_CHANGED: 'Language Changed',

  // Feedback Events
  FEEDBACK_SUBMITTED: 'Feedback Submitted',
  RESPONSE_RATED: 'Response Rated',
  FLAGGED_FOR_REVIEW: 'Flagged for Review',

  // Search Events
  ZIP_CODE_SEARCHED: 'Zip Code Searched',
  GIVEAWAY_RESULTS_VIEWED: 'Giveaway Results Viewed',

  // Widget Events
  WIDGET_LOADED: 'Widget Loaded',
  WIDGET_ERROR: 'Widget Error',

  // Admin Events
  ADMIN_PAGE_VIEWED: 'Admin Page Viewed',
  ADMIN_EXPORT_CLICKED: 'Admin Export Clicked',
  ADMIN_REVIEW_COMPLETED: 'Admin Review Completed',

  // Consent Events
  CONSENT_SHOWN: 'Consent Shown',
  CONSENT_ACCEPTED: 'Consent Accepted',
  CONSENT_DECLINED: 'Consent Declined',

  // Error Events
  ERROR_OCCURRED: 'Error Occurred',
  API_TIMEOUT: 'API Timeout',
  OFFLINE_FALLBACK: 'Offline Fallback Used',
};

/**
 * User properties for cohort analysis and personalization
 */
export const USER_PROPERTIES = {
  // Set once (never changes)
  FIRST_SEEN: '$created',
  INITIAL_LANGUAGE: 'Initial Language',
  INITIAL_REFERRER: 'Initial Referrer',

  // Updated on each session
  LAST_SEEN: 'Last Seen',
  SESSION_COUNT: 'Session Count',
  TOTAL_MESSAGES: 'Total Messages',
  TOTAL_FLOWS_COMPLETED: 'Total Flows Completed',
  PREFERRED_LANGUAGE: 'Preferred Language',

  // Behavioral flags
  IS_DEEP_EXPLORER: 'Is Deep Explorer',
  HAS_DONATED: 'Has Started Donation Flow',
  HAS_SEARCHED_GIVEAWAYS: 'Has Searched Giveaways',
  FEEDBACK_GIVEN_COUNT: 'Feedback Given Count',
};

/**
 * Flow IDs for consistent flow tracking
 */
export const FLOW_IDS = {
  GIVEAWAY: 'giveaway',
  DONATION: 'donation',
  INTERNET_HELP: 'internet-help',
  DIGITAL_SKILLS: 'digital-skills',
  TECH_SUPPORT: 'tech-support',
  COMMUNITY_RESOURCES: 'community-resources',
  HUMAN_SUPPORT: 'human-support',
};

/**
 * Button types for categorization
 */
export const BUTTON_TYPES = {
  QUICK_ACTION: 'quick_action',
  FOLLOW_UP: 'follow_up',
  EXTERNAL_LINK: 'external_link',
  NAVIGATION: 'navigation',
};

/**
 * Link categories for external link tracking
 */
export const LINK_CATEGORIES = {
  GIVEAWAY: 'giveaway',
  DONATION: 'donation',
  RESOURCE: 'resource',
  PARTNER: 'partner',
  SUPPORT: 'support',
};

/**
 * Funnel definitions for Mixpanel analysis
 * Use these to set up funnels in Mixpanel dashboard
 *
 * To configure in Mixpanel:
 * 1. Go to Mixpanel Dashboard > Funnels
 * 2. Create new funnel with the events listed below
 * 3. Set conversion window as specified
 */
export const FUNNEL_DEFINITIONS = {
  // Core user journey: First interaction to completed flow
  FIRST_SESSION_SUCCESS: {
    name: 'First Session Success',
    description: 'New user completes their first helpful flow',
    events: [
      'Session Started',
      'Chat Opened',
      'Message Sent',
      'Message Received',
      'Flow Completed',
    ],
    conversionWindow: '30 minutes',
    filters: {
      'Session Started': { 'Session Count': 1 },
    },
  },

  // Giveaway funnel: User finds a computer giveaway
  GIVEAWAY_SEARCH: {
    name: 'Giveaway Search Funnel',
    description: 'User searches for and views giveaway events',
    events: [
      'Flow Started',
      'Zip Code Searched',
      'Giveaway Results Viewed',
      'External Link Clicked',
    ],
    conversionWindow: '1 hour',
    filters: {
      'Flow Started': { flow_id: 'giveaway' },
      'External Link Clicked': { link_category: 'giveaway' },
    },
  },

  // Donation funnel: User learns about and starts donating
  DONATION_INTENT: {
    name: 'Donation Intent Funnel',
    description: 'User explores donation options and clicks through',
    events: [
      'Flow Started',
      'Button Clicked',
      'External Link Clicked',
    ],
    conversionWindow: '1 hour',
    filters: {
      'Flow Started': { flow_id: 'donation' },
      'External Link Clicked': { link_category: 'donation' },
    },
  },

  // Engagement funnel: User explores multiple topics
  DEEP_EXPLORATION: {
    name: 'Deep Exploration Funnel',
    description: 'User explores 3+ different topics in one session',
    events: [
      'Session Started',
      'Explore More Clicked',
      'Explore More Selected',
      'Explore More Clicked', // Second exploration
      'Explore More Selected',
      'Explore More Clicked', // Third exploration
    ],
    conversionWindow: '1 hour',
  },

  // Feedback funnel: User provides feedback
  FEEDBACK_LOOP: {
    name: 'Feedback Loop',
    description: 'User receives response and provides feedback',
    events: [
      'Message Sent',
      'Message Received',
      'Response Rated',
    ],
    conversionWindow: '10 minutes',
  },

  // Retention funnel: User returns for second session
  RETURN_VISIT: {
    name: 'Return Visit',
    description: 'User returns for a second session',
    events: [
      'Session Ended',
      'Session Started',
    ],
    conversionWindow: '7 days',
    filters: {
      'Session Started': { 'Session Count': { $gte: 2 } },
    },
  },

  // Error recovery: User experiences error but continues
  ERROR_RECOVERY: {
    name: 'Error Recovery',
    description: 'User continues after experiencing an error',
    events: [
      'Error Occurred',
      'Message Sent',
      'Message Received',
    ],
    conversionWindow: '5 minutes',
  },

  // Offline success: User gets help while offline
  OFFLINE_SUCCESS: {
    name: 'Offline Success',
    description: 'User successfully uses offline mode',
    events: [
      'Offline Fallback Used',
      'Message Received',
      'Response Rated',
    ],
    conversionWindow: '10 minutes',
    filters: {
      'Message Received': { is_offline_response: true },
    },
  },
};

/**
 * Key metrics to track in Mixpanel Insights
 */
export const KEY_METRICS = {
  // User engagement
  DAILY_ACTIVE_USERS: {
    name: 'Daily Active Users',
    event: 'Session Started',
    aggregation: 'unique_users',
  },
  MESSAGES_PER_SESSION: {
    name: 'Messages per Session',
    event: 'Message Sent',
    aggregation: 'average',
    groupBy: 'session',
  },
  FLOW_COMPLETION_RATE: {
    name: 'Flow Completion Rate',
    formula: 'Flow Completed / Flow Started * 100',
  },

  // User satisfaction
  POSITIVE_FEEDBACK_RATE: {
    name: 'Positive Feedback Rate',
    event: 'Response Rated',
    filter: { rating: 'positive' },
    formula: 'positive / total * 100',
  },

  // Feature adoption
  EXPLORE_MORE_USAGE: {
    name: 'Explore More Usage',
    event: 'Explore More Clicked',
    aggregation: 'count',
  },
  GIVEAWAY_SEARCH_RATE: {
    name: 'Giveaway Search Rate',
    event: 'Zip Code Searched',
    aggregation: 'unique_users',
  },

  // Technical health
  OFFLINE_FALLBACK_RATE: {
    name: 'Offline Fallback Rate',
    formula: 'Offline Fallback Used / Message Sent * 100',
  },
  API_TIMEOUT_RATE: {
    name: 'API Timeout Rate',
    formula: 'API Timeout / Message Sent * 100',
  },

  // Retention
  RETURN_RATE: {
    name: '7-Day Return Rate',
    description: 'Users who return within 7 days',
    filter: { 'Session Count': { $gte: 2 } },
  },
};
